import { IndependenceConfirmationPopup } from '@shared/ui';
import { useAudits, useConfirmIndependence, useRejectIndependence } from '@/api';
import { independenceRefusalAuditId, onIndependenceRefusal } from '@/lib/api/independenceRefusal';
import { useQueryClient } from '@tanstack/react-query';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

type RequestParams = {
  auditId: number;
  isAuditor: boolean;
  independenceStatus: boolean | null | undefined;
  isArchived?: boolean;
};

type IndependenceContextValue = {
  requestConfirmation: (params: RequestParams) => Promise<void>;
  /**
   * Asks for an audit the server has just refused for want of independence (#1215), whatever this
   * tab knew of the seat, and settles as `requestConfirmation` does. An audit declined here since
   * it was last opened is not asked again: it settles as declined at once.
   */
  askAgain: (auditId: number) => Promise<void>;
};

type PendingRequest = {
  auditId: number;
  /** What every caller waiting on this question is given: one question per audit at a time. */
  outcome: Promise<void>;
  resolve: () => void;
  reject: (error?: unknown) => void;
};

const IndependenceConfirmationContext = createContext<IndependenceContextValue>({
  requestConfirmation: () => Promise.resolve(),
  askAgain: () => Promise.reject(new Error('independence_unavailable')),
});

export const useIndependenceConfirmation = () => useContext(IndependenceConfirmationContext);

/** Where the dashboard's independence question stands for one audit. */
export type IndependenceGate = 'asking' | 'clear' | 'declined';

/**
 * A DEEP LINK ASKS TOO. The API refuses every audit route to an auditor seated on the audit who has
 * not confirmed independence, with `409 code: 'independenceRequired'`
 * (`middleware/attachAuditById.js`, DATA_FLOW.md §1). The audit list and the audit switcher ask
 * before they open an audit; a pasted link, a bookmark or a notification lands on the dashboard
 * directly, so the dashboard asks with this before its first read of the audit. `GET /audit`,
 * outside the audit routes, says whether the caller holds a seat and has confirmed. An audit it
 * does not list (no seat, or the list failed) is left to the audit read, as before.
 *
 * A REFUSAL ON THE OPEN DASHBOARD ASKS AGAIN (#1215). What this tab knew can be stale: the auditor
 * declined in another tab, a company admin viewing the audit was seated on it, this tab confirmed
 * earlier, or the list was read before the seat. The server's 409 then reaches a mounted page, and
 * the question is put over it (`askAgain`): confirming sends again every read the 409 refused;
 * declining or closing goes to the audit list. A refused write is not sent again.
 */
export const useIndependenceGate = (auditId: string): IndependenceGate => {
  const { requestConfirmation, askAgain } = useIndependenceConfirmation();
  const { data, isPending } = useAudits();
  const [answer, setAnswer] = useState<{ auditId: string; gate: IndependenceGate } | null>(null);
  /** Declined stays declined while the page leaves: the list's re-read must not ask again. */
  const declined = answer?.auditId === auditId && answer.gate === 'declined';

  const audit = useMemo(
    () =>
      data?.companies
        .flatMap((company) => company.audits)
        .find((entry) => String(entry.id) === auditId),
    [auditId, data],
  );
  const isAuditor = Boolean(audit?.isAuditor);
  const independenceStatus =
    audit?.myIndependenceStatus ?? audit?.isAuditor?.independenceConfirmed ?? null;
  const isArchived = audit?.archived;

  useEffect(() => {
    if (isPending || declined) return undefined;
    let current = true;
    requestConfirmation({
      auditId: Number(auditId),
      isAuditor,
      independenceStatus,
      isArchived,
    }).then(
      () => {
        if (current) setAnswer({ auditId, gate: 'clear' });
      },
      () => {
        if (current) setAnswer({ auditId, gate: 'declined' });
      },
    );
    return () => {
      current = false;
    };
  }, [
    auditId,
    isPending,
    declined,
    isAuditor,
    independenceStatus,
    isArchived,
    requestConfirmation,
  ]);

  useEffect(() => {
    let current = true;
    const stop = onIndependenceRefusal((refusedAuditId) => {
      if (String(refusedAuditId) !== auditId) return false;
      askAgain(refusedAuditId).catch(() => {
        if (current) setAnswer({ auditId, gate: 'declined' });
      });
      return true;
    });
    return () => {
      current = false;
      stop();
    };
  }, [askAgain, auditId]);

  return answer?.auditId === auditId ? answer.gate : 'asking';
};

export const IndependenceConfirmationProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const queryClient = useQueryClient();
  const [pendingRequest, setPendingRequest] = useState<PendingRequest | null>(null);
  /** The question on screen, for a caller in the same tick as the one that opened it. */
  const pendingRef = useRef<PendingRequest | null>(null);
  const { mutateAsync: confirmIndependence, isPending: isConfirming } = useConfirmIndependence();
  const { mutateAsync: rejectIndependence, isPending: isRejecting } = useRejectIndependence();
  const confirmedAuditsRef = useRef<Set<number>>(new Set());
  /**
   * Audits declined or closed in this tab since they were last opened: a refusal still landing for
   * one, the decline's own re-read among them, must not put the question straight back.
   */
  const declinedAuditsRef = useRef<Set<number>>(new Set());

  /** Opens the question for `auditId`, or joins it when it is already open for that audit. */
  const ask = useCallback((auditId: number) => {
    const open = pendingRef.current;
    if (open?.auditId === auditId) return open.outcome;
    let resolve: () => void = () => undefined;
    let reject: (error?: unknown) => void = () => undefined;
    const outcome = new Promise<void>((onConfirmed, onDeclined) => {
      resolve = onConfirmed;
      reject = onDeclined;
    });
    const request = { auditId, outcome, resolve, reject };
    pendingRef.current = request;
    setPendingRequest(request);
    return outcome;
  }, []);

  /** Takes the answered question off the screen, unless another has replaced it meanwhile. */
  const closeQuestion = useCallback((request: PendingRequest) => {
    if (pendingRef.current !== request) return;
    pendingRef.current = null;
    setPendingRequest(null);
  }, []);

  const requestConfirmation = useCallback(
    ({ auditId, isAuditor, independenceStatus, isArchived }: RequestParams) => {
      declinedAuditsRef.current.delete(auditId);
      if (
        !isAuditor ||
        independenceStatus === true ||
        confirmedAuditsRef.current.has(auditId) ||
        isArchived
      ) {
        return Promise.resolve();
      }

      return ask(auditId);
    },
    [ask],
  );

  const askAgain = useCallback(
    (auditId: number) => {
      if (declinedAuditsRef.current.has(auditId)) {
        return Promise.reject(new Error('independence_rejected'));
      }
      confirmedAuditsRef.current.delete(auditId);
      return ask(auditId);
    },
    [ask],
  );

  const handleConfirm = useCallback(async () => {
    const request = pendingRef.current;
    if (!request) return;

    try {
      await confirmIndependence({ auditId: request.auditId });
      confirmedAuditsRef.current.add(request.auditId);
      // Every read the server refused for want of this answer goes again (#1215).
      void queryClient.invalidateQueries({
        predicate: (query) => independenceRefusalAuditId(query.state.error) === request.auditId,
      });
      request.resolve();
    } catch (error) {
      console.error(error);
      request.reject(error);
    } finally {
      closeQuestion(request);
    }
  }, [confirmIndependence, queryClient, closeQuestion]);

  const handleReject = useCallback(async () => {
    const request = pendingRef.current;
    if (!request) return;
    // Before the answer goes out: the refusals its own re-reads draw land after it.
    declinedAuditsRef.current.add(request.auditId);

    try {
      await rejectIndependence({ auditId: request.auditId });
      request.reject(new Error('independence_rejected'));
    } catch (error) {
      console.error(error);
      // Settle the pending promise even when the backend call fails,
      // otherwise the caller's navigation handler hangs forever.
      request.reject(error);
    } finally {
      closeQuestion(request);
    }
  }, [rejectIndependence, closeQuestion]);

  // Closing the question settles it as declined, so a caller waiting on it (the dashboard's gate)
  // is never left waiting; the lists do nothing on either.
  const handleClose = useCallback(() => {
    const request = pendingRef.current;
    if (!request) return;
    declinedAuditsRef.current.add(request.auditId);
    request.reject(new Error('independence_dismissed'));
    closeQuestion(request);
  }, [closeQuestion]);

  const value = useMemo(
    () => ({
      requestConfirmation,
      askAgain,
    }),
    [requestConfirmation, askAgain],
  );

  return (
    <IndependenceConfirmationContext.Provider value={value}>
      {children}
      <IndependenceConfirmationPopup
        open={Boolean(pendingRequest)}
        confirmLoading={isConfirming}
        conflictLoading={isRejecting}
        onClose={handleClose}
        onConfirm={handleConfirm}
        onConflict={handleReject}
      />
    </IndependenceConfirmationContext.Provider>
  );
};
